// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, BackgroundImage } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { IconMail, IconArrowLeft, IconKey, IconCheck, IconPhoto } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { AuthIllustration } from '../components';

export default function ForgotIllustrated() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <Box style={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left side - Illustration */}
      <Box
        style={{
          width: '50%',
          display: 'none',
          position: 'relative',
          overflow: 'hidden',
        }}
        visibleFrom="md"
      >
        <BackgroundImage
          src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&q=80"
          style={{
            position: 'absolute',
            inset: 0,
          }}
        />
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${theme.colors.orange[9]}dd, ${theme.colors.yellow[8]}cc)`,
          }}
        />
        <Center h="100%" p="xl" style={{ position: 'relative', zIndex: 1 }}>
          <Stack align="center" gap="xl">
            <SlideIn direction="down" delay={200}>
              <AuthIllustration type="forgot-password" size={250} animated />
            </SlideIn>
            <FadeIn delay={400}>
              <Stack align="center" gap="sm">
                <Title order={2} c="white" ta="center">
                  Forgot Your Password?
                </Title>
                <Text c="white" ta="center" opacity={0.9} maw={350}>
                  It happens to the best of us. We'll help you get back into your account quickly.
                </Text>
              </Stack>
            </FadeIn>
          </Stack>
        </Center>
      </Box>

      {/* Right side - Form */}
      <Box
        style={{
          flex: 1,
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.white,
        }}
      >
        <Center mih="100vh" p="xl">
          <Container size="xs" w="100%">
            {submitted ? (
              <Stack gap="lg" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 20,
                    backgroundColor: 'var(--mantine-color-green-light)',
                  }}
                >
                  <IconCheck size={40} color="var(--mantine-color-green-6)" />
                </Center>

                <Stack align="center" gap="xs">
                  <Title order={2} ta="center">Check your email</Title>
                  <Text c="dimmed" ta="center" maw={300}>
                    We've sent a password reset link to <strong>{email}</strong>
                  </Text>
                </Stack>

                <Alert color="blue" variant="light" w="100%">
                  <Text size="sm">
                    Didn't receive the email? Check your spam folder or{' '}
                    <Anchor onClick={() => setSubmitted(false)}>try again</Anchor>
                  </Text>
                </Alert>

                <Button
                  component={Link}
                  to="/app/demo/auth-pages/login/illustrated"
                  variant="subtle"
                  leftSection={<IconArrowLeft size={16} />}
                >
                  Back to login
                </Button>
              </Stack>
            ) : (
              <Stack gap="lg">
                <Button
                  component={Link}
                  to="/app/demo/auth-pages/forgot-password"
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  w="fit-content"
                >
                  Back to variants
                </Button>

                <Stack align="center" gap="xs">
                  <Badge
                    leftSection={<IconPhoto size={12} />}
                    variant="gradient"
                    gradient={{ from: 'orange', to: 'yellow' }}
                    size="lg"
                  >
                    Illustrated Design
                  </Badge>
                  <Center
                    hiddenFrom="md"
                    style={{ marginTop: 'var(--mantine-spacing-md)' }}
                  >
                    <AuthIllustration type="forgot-password" size={120} animated />
                  </Center>
                  <Center
                    visibleFrom="md"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      backgroundColor: 'var(--mantine-color-orange-light)',
                      marginTop: 'var(--mantine-spacing-md)',
                    }}
                  >
                    <IconKey size={28} color="var(--mantine-color-orange-6)" />
                  </Center>
                  <Title order={2} ta="center" mt="sm">
                    Forgot password?
                  </Title>
                  <Text c="dimmed" size="sm" ta="center" maw={300}>
                    No worries, we'll send you reset instructions.
                  </Text>
                </Stack>

                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <TextInput
                      label="Email"
                      placeholder="Enter your email"
                      leftSection={<IconMail size={16} />}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      size="md"
                      radius="md"
                    />

                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      radius="md"
                      variant="gradient"
                      gradient={{ from: 'orange', to: 'yellow' }}
                    >
                      Send reset link
                    </Button>
                  </Stack>
                </form>

                <Center>
                  <Anchor component={Link} to="/app/demo/auth-pages/login/illustrated" size="sm">
                    <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Back to login
                  </Anchor>
                </Center>
              </Stack>
            )}
          </Container>
        </Center>
      </Box>
    </Box>
  );
}
